/**
 * PullProjectImagesPicker — Wave 5.1 (2026-02).
 *
 * The "PULL" side of the Work Table image flow. Browses the union of a
 * project's photos across three sources — field visits, desktop
 * progress-log uploads, and email attachments — and lets the user
 * insert one or more into the current canvas.
 *
 * Provenance travels with each pick: the caller receives
 * `{ image_url, source, source_photo_id, caption }` per selection so
 * the Work Table item can back-link to its origin.
 *
 * Uses the shared `/api/projects/:id/property-history` endpoint, which
 * already surfaces the three-source union (see
 * `backend/routes/site_visits.py:get_project_property_history`).
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Camera, Monitor, Mail, Check, Landmark, Award, Search } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCE_META = {
  field:   { label: 'Field', icon: Camera,  color: 'bg-indigo-50 text-indigo-800 border-indigo-200' },
  desktop: { label: 'Desktop', icon: Monitor, color: 'bg-slate-50 text-slate-700 border-slate-200' },
  email:   { label: 'Email',   icon: Mail,    color: 'bg-teal-50 text-teal-800 border-teal-200' },
};

function thumbFor(rawUrl, width = 300) {
  if (!rawUrl || typeof rawUrl !== 'string') return rawUrl;
  if (rawUrl.startsWith('data:') || rawUrl.startsWith('blob:')) return rawUrl;
  const absolute = rawUrl.startsWith('http') ? rawUrl : `${BACKEND_URL}${rawUrl}`;
  if (rawUrl.includes('/api/uploads/')) {
    // 2026-07-15 edge-cache leak fix — `?tw=` (not `?w=`) so every
    // previously edge-cached URL becomes unreachable through this app.
    const sep = absolute.includes('?') ? '&' : '?';
    return `${absolute}${sep}tw=${width}`;
  }
  try {
    const u = new URL(absolute);
    const h = u.hostname.toLowerCase();
    if (h.endsWith('dropbox.com') || h.endsWith('dropboxusercontent.com') || h === 'images.unsplash.com') {
      return `${BACKEND_URL}/api/photo-thumb?url=${encodeURIComponent(absolute)}&w=${width}`;
    }
  } catch (_e) { /* fall through */ }
  return absolute;
}

export default function PullProjectImagesPicker({ open, onClose, projectId, onPick, multi = true }) {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState(null);
  const [source, setSource] = useState('all');
  const [onlyAsBuilt, setOnlyAsBuilt] = useState(false);
  const [onlyPortfolio, setOnlyPortfolio] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState({});
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!open || !projectId) return;
    setSelected({});
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/projects/${projectId}/property-history`)
      .then((r) => setHistory(r.data))
      .catch(() => toast.error('Could not load project photos.'))
      .finally(() => setLoading(false));
  }, [open, projectId]);

  const allPhotos = useMemo(() => {
    if (!history) return [];
    const items = [];
    // Field photos — from current + all as-built visits. We already
    // have current_conditions (newest visit) and the per-category / room
    // rollups. Rather than re-fetching, walk portfolio_candidates +
    // current_conditions + as_built_by_category values for coverage.
    const seen = new Set();
    const push = (p) => {
      const k = `${p.source || 'field'}:${p.id || p.url}`;
      if (seen.has(k)) return;
      seen.add(k);
      items.push(p);
    };
    (history.current_conditions || []).forEach(push);
    Object.values(history.as_built_by_category || {}).forEach(push);
    Object.values(history.as_built_by_room || {}).forEach(push);
    (history.portfolio_candidates || []).forEach(push);
    (history.desktop_photos || []).forEach(push);
    (history.email_photos || []).forEach(push);
    return items;
  }, [history]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allPhotos.filter((p) => {
      if (source !== 'all' && (p.source || 'field') !== source) return false;
      if (onlyAsBuilt && !p.is_as_built) return false;
      if (onlyPortfolio && !p.is_portfolio_candidate) return false;
      if (q) {
        const hay = `${p.caption || ''} ${p.room || ''} ${p.category || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [allPhotos, source, onlyAsBuilt, onlyPortfolio, search]);

  const toggle = (p) => {
    const k = p.id || p.url;
    if (multi) {
      setSelected((s) => {
        const next = { ...s };
        if (next[k]) delete next[k]; else next[k] = p;
        return next;
      });
    } else {
      commitPicks([p]);
    }
  };

  const commitPicks = async (picks) => {
    if (!picks.length) return;
    setAdding(true);
    try {
      for (const p of picks) {
        await onPick({
          image_url: p.url,
          source: p.source || 'field',
          source_photo_id: p.id,
          caption: p.caption,
          visit_id: p.visit_id,
        });
      }
      toast.success(`Added ${picks.length} image${picks.length > 1 ? 's' : ''} to the canvas.`);
      onClose();
    } catch (_e) {
      toast.error('Could not add one or more images.');
    } finally {
      setAdding(false);
    }
  };

  const picked = Object.values(selected);
  const counts = useMemo(() => {
    const c = { all: allPhotos.length, field: 0, desktop: 0, email: 0 };
    allPhotos.forEach((p) => { c[p.source || 'field'] = (c[p.source || 'field'] || 0) + 1; });
    return c;
  }, [allPhotos]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col" data-testid="pull-project-images-picker">
        <DialogHeader>
          <DialogTitle className="text-base">Pull an image from this project</DialogTitle>
          <p className="text-xs text-slate-500">
            Browse the union of field visits, desktop uploads, and email attachments. Provenance is preserved on the Work Table item.
          </p>
        </DialogHeader>

        {/* Filter strip */}
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search caption / room / trade…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-7 text-xs border border-slate-300 rounded py-1.5"
              data-testid="pull-picker-search"
            />
          </div>
          <div className="flex gap-1">
            {[
              { id: 'all',     label: `All (${counts.all})` },
              { id: 'field',   label: `Field (${counts.field || 0})` },
              { id: 'desktop', label: `Desktop (${counts.desktop || 0})` },
              { id: 'email',   label: `Email (${counts.email || 0})` },
            ].map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSource(s.id)}
                className={`text-[11px] px-2 py-1 rounded-full border ${source === s.id ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300'}`}
                data-testid={`pull-picker-source-${s.id}`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <label className="text-[11px] text-slate-600 flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={onlyAsBuilt} onChange={(e) => setOnlyAsBuilt(e.target.checked)} data-testid="pull-picker-asbuilt" />
            As-built only
          </label>
          <label className="text-[11px] text-slate-600 flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={onlyPortfolio} onChange={(e) => setOnlyPortfolio(e.target.checked)} data-testid="pull-picker-portfolio" />
            Portfolio only
          </label>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto min-h-[280px]">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-xs text-slate-500 text-center py-12" data-testid="pull-picker-empty">
              No images match those filters.
            </p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 pt-2">
              {filtered.map((p) => {
                const k = p.id || p.url;
                const isSelected = !!selected[k];
                const meta = SOURCE_META[p.source || 'field'] || SOURCE_META.field;
                const MetaIcon = meta.icon;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => toggle(p)}
                    className={`relative group text-left bg-white border rounded overflow-hidden shadow-sm hover:shadow-md transition-all ${isSelected ? 'border-indigo-600 ring-2 ring-indigo-500/40' : 'border-slate-200'}`}
                    data-testid={`pull-picker-photo-${k}`}
                  >
                    <div className="relative aspect-[4/3] bg-slate-100">
                      <img
                        src={thumbFor(p.url, 300)}
                        alt={p.caption || ''}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div className={`absolute top-1 left-1 text-[9px] font-semibold px-1.5 py-0.5 rounded flex items-center gap-0.5 border ${meta.color}`}>
                        <MetaIcon className="w-2.5 h-2.5" /> {meta.label}
                      </div>
                      {p.is_as_built && (
                        <div className="absolute bottom-1 left-1 bg-indigo-600 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded flex items-center gap-0.5">
                          <Landmark className="w-2.5 h-2.5" /> AS-BUILT
                        </div>
                      )}
                      {p.is_portfolio_candidate && (
                        <div className="absolute bottom-1 right-1 bg-amber-500 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded flex items-center gap-0.5">
                          <Award className="w-2.5 h-2.5" />
                        </div>
                      )}
                      {isSelected && (
                        <div className="absolute top-1 right-1 w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center">
                          <Check className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                    <div className="p-1">
                      <p className="text-[10px] text-slate-700 line-clamp-1">{p.caption || (p.room || 'Photo')}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 pt-2 flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs text-slate-500">
            {multi ? (
              <>
                <Badge variant="outline" className="text-[10px] mr-1">{picked.length}</Badge>
                selected
              </>
            ) : 'Click any image to add it.'}
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            {multi && (
              <Button
                onClick={() => commitPicks(picked)}
                disabled={!picked.length || adding}
                data-testid="pull-picker-add"
              >
                {adding ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                Add {picked.length || ''} image{picked.length === 1 ? '' : 's'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
